import { FetchRequestOptions } from '../types';
import { merge } from '../utils';

/**
 * Default options for the HTTP service.
 */
const defaultOptions: FetchRequestOptions = {
  // method: 'POST', // *GET, POST, PUT, DELETE, etc.
  // mode: 'cors', // no-cors, *cors, same-origin
  // cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  // credentials: 'same-origin', // include, *same-origin, omit
  headers: {
    'Content-Type': 'application/json',
    // 'Content-Type': 'application/x-www-form-urlencoded',
  },
  // redirect: 'follow', // manual, *follow, error
  // referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
  // body: JSON.stringify(data) // body data type must match 'Content-Type' header
};

/**
 * Performs an HTTP fetch request.
 * @param url - The URL to fetch.
 * @param options - The fetch request options.
 * @param serviceOptions - Additional service options.
 * @returns A promise that resolves to the response data.
 */
export async function httpFetch(url: string, options: FetchRequestOptions = {}, serviceOptions?: any): Promise<any> {
  try {
    const httpOptions = merge({ ...defaultOptions }, options);
    // console.log('httpFetch', url, options, httpOptions);
    const httpResponse = await fetch(url, httpOptions);
    // console.log('httpResponse', httpResponse);
    if (!httpResponse.ok) {
      throw httpResponse;
    }
    const response = await httpResponse.json();
    return response;
  } catch (error: any) {
    if (typeof error === 'object' && error.status) {
      console.log('httpFetch.error', options.method, url, error.status, error.statusText);
    } else {
      console.log('httpFetch.error', options.method, url, error);
    }
    throw (error);
  }
}

/**
 * Performs an HTTP GET request.
 * @param url - The URL to fetch.
 * @param options - The fetch request options.
 * @returns A promise that resolves to the response data.
 */
export async function httpGet(url: string, options: FetchRequestOptions = {}): Promise<any> {
  return await httpFetch(url, { ...options, method: 'GET' });
}

/**
 * Performs an HTTP POST request.
 * @param url - The URL to fetch.
 * @param payload - The payload to send in the request body.
 * @param options - The fetch request options.
 * @returns A promise that resolves to the response data.
 */
export async function httpPost(url: string, payload: any, options: FetchRequestOptions = {}): Promise<any> {
  return await httpFetch(url, { ...options, method: 'POST', body: JSON.stringify(payload) });
}

/**
 * Performs an HTTP PUT request.
 * @param url - The URL to fetch.
 * @param payload - The payload to send in the request body.
 * @param options - The fetch request options.
 * @returns A promise that resolves to the response data.
 */
export async function httpPut(url: string, payload: any, options: FetchRequestOptions = {}): Promise<any> {
  return await httpFetch(url, { ...options, method: 'PUT', body: JSON.stringify(payload) });
}

/**
 * Performs an HTTP PATCH request.
 * @param url - The URL to fetch.
 * @param payload - The payload to send in the request body.
 * @param options - The fetch request options.
 * @returns A promise that resolves to the response data.
 */
export async function httpPatch(url: string, payload: any, options: FetchRequestOptions = {}): Promise<any> {
  return await httpFetch(url, { ...options, method: 'PATCH', body: JSON.stringify(payload) });
}

/**
 * Performs an HTTP DELETE request.
 * @param url - The URL to fetch.
 * @param options - The fetch request options.
 * @returns A promise that resolves to the response data.
 */
export async function httpDelete(url: string, options: FetchRequestOptions = {}): Promise<any> {
  return await httpFetch(url, { ...options, method: 'DELETE' });
}
